import React, { useState, useMemo } from 'react';
import { Switch } from "@heroui/react";

export default function BarChart({ isDualRange, rawData }) {
    const chartData = rawData;
    const caption =
        'Grouped bar chart compares the total lead count for Range 0 and Range 1 for each source category, filtered by the selection above.';

    const transformedData = useMemo(() => {
        const range0Raw = chartData?.range_0_data;
        const range1Raw = chartData?.range_1_data;

        const range0 = Array.isArray(range0Raw)
            ? range0Raw
            : range0Raw
                ? Object.values(range0Raw)
                : [];

        const range1 = Array.isArray(range1Raw)
            ? range1Raw
            : range1Raw
                ? Object.values(range1Raw)
                : [];

        const range0Map = new Map(range0.map((d) => [d.name, d.count]));
        const range1Map = new Map(range1.map((d) => [d.name, d.count]));

        const allNames = new Set([
            ...range0.map((d) => d.name),
            ...(isDualRange ? range1.map((d) => d.name) : []),
        ]);

        return Array.from(allNames).map((name) => ({
            category: name,
            r1: range0Map.get(name) || 0,
            r2: isDualRange ? range1Map.get(name) || 0 : 0,
        }));
    }, [chartData, isDualRange]);

    return (
        <div className="w-full">
            <GroupedBarChart isDualRange={isDualRange} data={transformedData} caption={caption} />
        </div>
    );
}

const isLabelLong = (text) => text?.length >= 9;

/**
 * A highly polished, custom-animated grouped bar chart.
 */
const GroupedBarChart = ({ isDualRange, data, caption }) => {
    const [animateBars, setAnimateBars] = useState(false);
    const [viewAsPercent, setViewAsPercent] = useState(false);

    React.useEffect(() => {
        const timeout = setTimeout(() => setAnimateBars(true), 100);
        return () => clearTimeout(timeout);
    }, []);

    const totalR1 = useMemo(() => data.reduce((sum, item) => sum + (item.r1 || 0), 0), [data]);
    const totalR2 = useMemo(() => data.reduce((sum, item) => sum + (item.r2 || 0), 0), [data]);

    const processedData = useMemo(() => {
        return data.map(item => {
            const r1Val = viewAsPercent ? (totalR1 > 0 ? (item.r1 / totalR1) * 100 : 0) : item.r1;
            const r2Val = viewAsPercent ? (totalR2 > 0 ? (item.r2 / totalR2) * 100 : 0) : item.r2;
            return {
                category: item.category,
                r1: r1Val,
                r2: r2Val,
                r1Raw: item.r1,
                r2Raw: item.r2
            };
        });
    }, [data, viewAsPercent, totalR1, totalR2]);

    const maxVal = Math.max(0, ...processedData.flatMap((d) => [d.r1, d.r2]));

    const { yAxisMax, yAxisLabels } = useMemo(() => {
        if (maxVal === 0) {
            return { yAxisMax: 50, yAxisLabels: [50, 40, 30, 20, 10, 0] };
        }

        const numSteps = 4;
        const roughStep = maxVal / numSteps;
        const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
        const roundedStep = Math.ceil(roughStep / magnitude) * magnitude;
        const newYAxisMax = roundedStep * numSteps;

        const labels = [];
        for (let i = newYAxisMax; i >= 0; i -= roundedStep) {
            labels.push(i);
        }

        return { yAxisMax: newYAxisMax, yAxisLabels: labels };
    }, [maxVal]);

    const LegendItem = ({ colorClass, ringClass, text }) => (
        <div className="flex items-center space-x-2 bg-slate-50/60 px-3 py-1.5 rounded-full border border-slate-100 hover:bg-slate-50 hover:border-slate-200 transition-all duration-200">
            <div className={`w-2.5 h-2.5 rounded-full ${colorClass} ring-4 ${ringClass}`}></div>
            <span className="text-xs font-semibold text-slate-600 select-none">{text}</span>
        </div>
    );

    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-100/50 w-full p-6 sm:p-8 transition-all duration-300">
            {/* Header Area */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 border-b border-slate-50 pb-5">
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent">
                        Source Wise Leads Count
                    </h2>
                    <p className="text-xs text-slate-400 mt-1">
                        Performance comparison across different acquisition channels
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex flex-wrap gap-2.5">
                        {isDualRange && (
                            <LegendItem 
                                colorClass="bg-indigo-500" 
                                ringClass="ring-indigo-100/60" 
                                text="Range 1" 
                            />
                        )}
                        <LegendItem 
                            colorClass={isDualRange ? "bg-teal-500" : "bg-indigo-500"} 
                            ringClass={isDualRange ? "ring-teal-100/60" : "ring-indigo-100/60"} 
                            text={isDualRange ? "Range 2" : "Current Range"} 
                        />
                    </div>
                    <div className="flex items-center gap-2 border-l border-slate-200 pl-4 h-6">
                        <span className="text-xs font-semibold text-slate-500 select-none">View as %</span>
                        <Switch
                            isSelected={viewAsPercent}
                            onValueChange={(val) => setViewAsPercent(val)}
                            size="sm"
                        />
                    </div>
                </div>
            </div>

            {/* Scrollable Chart Frame */}
            <div className="flex overflow-x-auto overflow-y-hidden w-full pb-4 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                <div className="flex-1 pl-12 pr-4 min-w-[640px]">
                    
                    {/* Y-Axis Grid & Bars Frame */}
                    <div className="relative h-64 sm:h-80 pt-10">
                        
                        {/* Horizontal Gridlines & Y-Axis Numbers */}
                        <div className="absolute inset-0 top-10 flex flex-col justify-between">
                            {yAxisLabels.map((label) => (
                                <div key={label} className="relative">
                                    <span className="absolute right-[calc(100%+0.75rem)] -translate-y-1/2 text-[10px] font-bold text-slate-400 select-none text-right">
                                        {viewAsPercent ? `${label.toFixed(0)}%` : (label >= 1000 ? `${(label / 1000).toFixed(0)}k` : label)}
                                    </span>
                                    <div
                                        className={`w-full ${
                                            label === 0 
                                                ? 'h-[2px] bg-slate-300' 
                                                : 'h-[1px] border-t border-dashed border-slate-100'
                                        }`}
                                    ></div>
                                </div>
                            ))}
                        </div>

                        {/* Y-Axis Line */}
                        <div className="absolute left-0 top-10 bottom-0 w-[2px] bg-slate-300"></div>

                        {/* Staggered Animated Columns Container */}
                        <div className="absolute inset-0 top-10 flex justify-around items-end z-10">
                            {processedData.map((item, idx) => {
                                const r1Height = yAxisMax > 0 ? (item.r1 / yAxisMax) * 100 : 0;
                                const r2Height = yAxisMax > 0 ? (item.r2 / yAxisMax) * 100 : 0;
                                const staggerDelay = `${idx * 45}ms`;

                                const r1Style = {
                                    height: animateBars ? `${r1Height}%` : '0%',
                                    minHeight: item.r1 > 0 ? '4px' : '0px',
                                    transition: 'height 0.85s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    transitionDelay: staggerDelay
                                };

                                const r2Style = {
                                    height: animateBars ? `${r2Height}%` : '0%',
                                    minHeight: item.r2 > 0 ? '4px' : '0px',
                                    transition: 'height 0.85s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    transitionDelay: staggerDelay
                                };

                                return (
                                    <div
                                        key={item.category}
                                        className="flex items-end justify-center gap-1.5 sm:gap-2.5 h-full w-full px-1 sm:px-2 rounded-t-lg"
                                        style={{ maxWidth: '100px' }}
                                    >
                                        {isDualRange ? (
                                            <>
                                                {/* Range 1 Column (Indigo Gradient) */}
                                                <div
                                                    className="w-1/2 bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-lg relative shadow-md shadow-indigo-500/10"
                                                    style={r1Style}
                                                >
                                                    {item.r1 > 0 && animateBars && (
                                                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 pb-1.5 text-[10px] font-bold text-slate-500 pointer-events-none">
                                                            {viewAsPercent ? `${item.r1.toFixed(2)}%` : item.r1.toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Range 2 Column (Teal Gradient) */}
                                                <div
                                                    className="w-1/2 bg-gradient-to-t from-teal-600 to-teal-400 rounded-t-lg relative shadow-md shadow-teal-500/10"
                                                    style={r2Style}
                                                >
                                                    {item.r2 > 0 && animateBars && (
                                                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 pb-1.5 text-[10px] font-bold text-slate-500 pointer-events-none">
                                                            {viewAsPercent ? `${item.r2.toFixed(2)}%` : item.r2.toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            // Single Range (Indigo Gradient only)
                                            <div
                                                className="w-2/3 bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-lg relative shadow-md shadow-indigo-500/10"
                                                style={r1Style}
                                            >
                                                {item.r1 > 0 && animateBars && (
                                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 pb-1.5 text-[10px] font-bold text-slate-500 pointer-events-none">
                                                        {viewAsPercent ? `${item.r1.toFixed(2)}%` : item.r1.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Rotated X-Axis Labels */}
                    <div className="flex justify-around mt-1 min-h-24">
                        {processedData.map((item) => (
                            <div
                                key={item.category}
                                className="w-full h-24"
                                style={{ maxWidth: '100px' }}
                            >
                                <span className={`text-[10px] sm:text-xs font-semibold text-slate-400 transform -rotate-45 ${isLabelLong(item.category) === true ? 'justify-end pt-8' : 'justify-center pt-4'} whitespace-nowrap flex`}>
                                    {item.category}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Bottom Caption Info */}
            {caption && (
                <p className="text-xs text-slate-400 mt-6 pt-5 border-t border-slate-100 leading-relaxed select-none">
                    {caption}
                </p>
            )}
        </div>
    );
};